import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Zap, LogIn } from "lucide-react";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../hooks/use-toast";

export default function DashboardLogin() {
  const nav = useNavigate();
  const loc = useLocation();
  const { login } = useAuth();
  const { toast } = useToast();
  const [form, setForm] = useState({ email: "", password: "" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(form.email, form.password);
      const to = loc.state?.from || "/dashboard";
      nav(to, { replace: true });
    } catch (err) {
      const msg = err?.response?.data?.detail || "Login failed";
      toast({ title: "Sign in failed", description: String(msg) });
    } finally {
      setBusy(false);
    }
  };

  const cls = "bg-white/[0.03] border-white/10 focus-visible:border-cyan-400/50 focus-visible:ring-cyan-400/20 text-white placeholder:text-slate-500 h-11";

  return (
    <div className="min-h-screen hero-bg relative overflow-hidden flex items-center justify-center px-6">
      <div className="absolute inset-0 stars" />
      <div className="absolute inset-0 grid-overlay" />

      <div className="relative w-full max-w-md">
        <Link to="/" className="flex items-center gap-3 justify-center mb-8">
          <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-sky-500 to-cyan-400 flex items-center justify-center shadow-lg shadow-cyan-500/25">
            <Zap className="w-6 h-6 text-white" strokeWidth={2.5} />
          </div>
          <div className="leading-tight">
            <div className="font-display font-bold text-white text-lg">FortressPoint</div>
            <div className="text-[11px] tracking-widest uppercase text-cyan-300/80">Admin Dashboard</div>
          </div>
        </Link>

        <form onSubmit={submit} className="glass rounded-2xl p-8">
          <h1 className="font-display text-2xl font-bold text-white">Welcome back</h1>
          <p className="mt-1 text-sm text-slate-400">Sign in to your FortressPoint admin dashboard.</p>

          <div className="mt-6 space-y-3">
            <label className="block">
              <span className="block text-[11px] uppercase tracking-widest text-slate-400 font-semibold mb-2">Email</span>
              <Input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@company.com" className={cls} />
            </label>
            <label className="block">
              <span className="block text-[11px] uppercase tracking-widest text-slate-400 font-semibold mb-2">Password</span>
              <Input type="password" required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="••••••••" className={cls} />
            </label>
          </div>

          <Button type="submit" disabled={busy} className="mt-6 w-full h-12 btn-primary rounded-xl font-semibold border-0">
            <LogIn className="w-4 h-4 mr-2" /> {busy ? "Signing in..." : "Sign In"}
          </Button>

          <p className="mt-5 text-center text-sm text-slate-400">
            No account yet?{" "}
            <Link to="/dashboard/signup" className="text-cyan-300 hover:text-cyan-200 font-semibold">Create one</Link>
          </p>
        </form>
      </div>
    </div>
  );
}
